import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import SendIcon from "@mui/icons-material/Send";
import { useState } from "react";
import { contact } from "../../../../utils/siteContent";
import Reveal from "../../../commons/reveal/reveal";
import SectionHeading from "../../../commons/sectionHeading/sectionHeading";
import { StyledContact } from "../../../styled/contact";
import { StyledLayout } from "../../../styled/layout";

const socialIcons = {
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  facebook: FacebookIcon,
};

// Endpoint que envía el correo (PHP en el mismo hosting). Se puede sobreescribir
// con la variable de entorno VITE_CONTACT_ENDPOINT en tiempo de build.
const CONTACT_ENDPOINT =
  import.meta.env.VITE_CONTACT_ENDPOINT || "/sendmail.php";

const Contact = () => {
  // idle | sending | sent | error
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;

    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      name: (fd.get("name") || "").toString(),
      company: (fd.get("company") || "").toString(),
      email: (fd.get("email") || "").toString(),
      interest: (fd.get("interest") || "").toString(),
      message: (fd.get("message") || "").toString(),
      website: (fd.get("website") || "").toString(), // honeypot anti-spam
    };

    setStatus("sending");
    setErrorMsg("");

    const postOnce = () =>
      fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

    try {
      let res = await postOnce();
      let text = await res.text();

      // Algunos hostings (Bluehost/ModSecurity) interceptan el POST con un
      // "challenge" anti-bot: responden un <script> que setea una cookie y
      // recarga la página. Como fetch no ejecuta ese script, lo emulamos
      // seteando la cookie que pide y reintentamos una sola vez.
      if (!res.ok) {
        const m = text.match(/document\.cookie\s*=\s*"([^"]+)"/);
        if (m && /humans_/i.test(m[1])) {
          document.cookie = m[1] + "; path=/; SameSite=Lax";
          res = await postOnce();
          text = await res.text();
        }
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (_) {
        data = {};
      }

      if (res.ok && data.ok) {
        setStatus("sent");
      } else {
        setStatus("error");
        setErrorMsg(
          data.error || "No se pudo enviar el mensaje. Intentá nuevamente.",
        );
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        "No se pudo conectar con el servidor. Revisá tu conexión e intentá de nuevo.",
      );
    }
  };

  return (
    <StyledLayout.Section id="contacto">
      <StyledLayout.Container>
        <StyledContact.Inner>
          <Reveal>
            <StyledContact.Info>
              <SectionHeading
                tag="Contacto"
                title={
                  <>
                    Hablemos de tu{" "}
                    <StyledLayout.GradientText>
                      próximo proyecto
                    </StyledLayout.GradientText>
                  </>
                }
                lead="Contanos sobre tu organización y un especialista te contactará para asesorarte sin cargo."
              />

              <StyledContact.Methods>
                <StyledContact.Method href={`tel:${contact.phoneHref}`}>
                  <span className="method-icon">
                    <PhoneIcon />
                  </span>
                  <span>
                    <small>Teléfono</small>
                    <strong>{contact.phone}</strong>
                  </span>
                </StyledContact.Method>
                <StyledContact.Method href={`mailto:${contact.email}`}>
                  <span className="method-icon">
                    <MailOutlineIcon />
                  </span>
                  <span>
                    <small>Email</small>
                    <strong>{contact.email}</strong>
                  </span>
                </StyledContact.Method>
              </StyledContact.Methods>

              <StyledContact.Socials>
                {contact.socials.map((s) => {
                  const Ic = socialIcons[s.icon];
                  return (
                    <StyledContact.Social
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={s.name}
                      title={`${s.name} · ${s.handle}`}>
                      <Ic />
                    </StyledContact.Social>
                  );
                })}
              </StyledContact.Socials>
            </StyledContact.Info>
          </Reveal>

          <Reveal delay={0.1}>
            <StyledContact.FormWrap>
              {status === "sent" ? (
                <StyledContact.Success>
                  <CheckCircleOutlineIcon />
                  <h3>¡Gracias por escribirnos!</h3>
                  <p>
                    Recibimos tu consulta. Un especialista de Access te contactará
                    a la brevedad.
                  </p>
                </StyledContact.Success>
              ) : (
                <StyledContact.Form onSubmit={handleSubmit}>
                  {/* Honeypot anti-spam: invisible para personas, tentador para bots */}
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      width: 1,
                      height: 1,
                      opacity: 0,
                    }}
                  />
                  <StyledContact.Field>
                    <label htmlFor="name">Nombre y apellido</label>
                    <input id="name" name="name" type="text" required placeholder="Tu nombre" />
                  </StyledContact.Field>
                  <StyledContact.FieldRow>
                    <StyledContact.Field>
                      <label htmlFor="company">Empresa</label>
                      <input id="company" name="company" type="text" placeholder="Tu organización" />
                    </StyledContact.Field>
                    <StyledContact.Field>
                      <label htmlFor="email">Email</label>
                      <input id="email" name="email" type="email" required placeholder="nombre@empresa.com" />
                    </StyledContact.Field>
                  </StyledContact.FieldRow>
                  <StyledContact.Field>
                    <label htmlFor="interest">Me interesa</label>
                    <select id="interest" name="interest" defaultValue="">
                      <option value="" disabled>
                        Elegí una opción
                      </option>
                      <option>Prestadores de Medicina Laboral</option>
                      <option>Servicios Médicos de Empresas</option>
                      <option>Aseguradoras de Riesgo del Trabajo (ART)</option>
                      <option>Sumarme al equipo (enviar CV)</option>
                      <option>Otra consulta</option>
                    </select>
                  </StyledContact.Field>
                  <StyledContact.Field>
                    <label htmlFor="message">Mensaje</label>
                    <textarea id="message" name="message" rows="4" placeholder="Contanos cómo podemos ayudarte" />
                  </StyledContact.Field>
                  <StyledContact.SubmitWrap>
                    <StyledLayout.Button
                      as="button"
                      type="submit"
                      variant="primary"
                      disabled={status === "sending"}
                      onClick={(e) => {
                        // StyledLayout.Button es styled("a"): en este setup MUI
                        // ignora as="button" y renderiza un <a>, que no envía el
                        // formulario. Si detectamos que es un <a>, disparamos el
                        // submit manualmente (respetando validación required).
                        if (e.currentTarget.tagName === "A") {
                          e.preventDefault();
                          if (status === "sending") return;
                          const form = e.currentTarget.closest("form");
                          if (form && typeof form.requestSubmit === "function") {
                            form.requestSubmit();
                          }
                        }
                      }}
                      style={{
                        width: "100%",
                        opacity: status === "sending" ? 0.7 : 1,
                        cursor: status === "sending" ? "wait" : "pointer",
                      }}>
                      {status === "sending" ? (
                        "Enviando…"
                      ) : (
                        <>
                          Solicitá asesoramiento <SendIcon />
                        </>
                      )}
                    </StyledLayout.Button>
                  </StyledContact.SubmitWrap>
                  {status === "error" && (
                    <StyledContact.Note
                      role="alert"
                      style={{ color: "#d32f2f", fontWeight: 600 }}>
                      {errorMsg}
                    </StyledContact.Note>
                  )}
                  <StyledContact.Note>
                    Al enviar aceptás ser contactado por el equipo de Access
                    Informática.
                  </StyledContact.Note>
                </StyledContact.Form>
              )}
            </StyledContact.FormWrap>
          </Reveal>
        </StyledContact.Inner>
      </StyledLayout.Container>
    </StyledLayout.Section>
  );
};

export default Contact;
